import React, { useState, useEffect } from "react";
import axios from "axios";
import { Printer, X, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = `${window.location.origin}/aarifi-logo-full.png`;

const fmtUS = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};
const fmtDob = (s) => (!s ? "" : s.includes("/") ? s : fmtUS(s));

/** Full 1099-NEC visual — used inside the modal print area */
export const ReceiptDocument = ({ data }) => {
  if (!data) return null;
  const { inspector, week, orders, totals, account_number } = data;
  return (
    <div className="receipt-print bg-white mx-auto text-[#1F2937]" style={{ maxWidth: "1040px", padding: "28px 32px" }} data-testid="receipt-document">
      {/* Header */}
      <div className="grid grid-cols-3 items-start gap-4 pb-4 border-b-2 border-[#0B2D5B]">
        <div className="col-span-1">
          <img src={LOGO_URL} alt="AARIFI" style={{ height: 80 }} />
          <p className="text-[11px] font-semibold mt-1 text-[#0B2D5B]">10 Bergen St</p>
          <p className="text-[11px] font-semibold text-[#0B2D5B]">Providence, RI 02908</p>
        </div>
        <div className="col-span-1 text-center">
          <h1 style={{ color: "#0B2D5B", fontSize: 44, fontWeight: 800, letterSpacing: "-1px", lineHeight: 1 }}>1099-NEC</h1>
          <p style={{ color: "#0B2D5B", fontSize: 14, fontWeight: 700, marginTop: 2 }}>Nonemployee Compensation</p>
          <p style={{ color: "#0B2D5B", fontSize: 13, marginTop: 6 }}>Tax Year <strong>{week.tax_year}</strong></p>
        </div>
        <div className="col-span-1 text-right">
          <p style={{ color: "#0B2D5B", fontSize: 18, fontWeight: 700 }}>Copy B</p>
          <p style={{ color: "#0B2D5B", fontSize: 14, fontWeight: 600 }}>For Recipient</p>
        </div>
      </div>

      {/* Inspector info + Amount */}
      <div className="grid grid-cols-2 gap-5 mt-5">
        <div>
          <div className="bg-[#0B2D5B] text-white text-[11px] font-bold px-3 py-1.5 uppercase tracking-wide">Recipient (Inspector) Information</div>
          <table className="w-full text-[11.5px]" style={{ borderCollapse: "collapse" }}>
            <tbody>
              {[
                ["Inspector Name:", inspector.code],
                ["Full Name:", inspector.full_name],
                ["Email:", inspector.email || "-"],
                ["Phone:", inspector.phone || "-"],
                ["Date of Birth:", fmtDob(inspector.date_of_birth)],
                ["Active Status:", inspector.active],
                ["Payment Type:", inspector.payment_type],
              ].map(([l, v], i) => (
                <tr key={i}>
                  <td style={{ padding: "5px 8px", border: "1px solid #0B2D5B", color: "#0B2D5B", fontWeight: 700, width: "38%", fontSize: "11px" }}>{l}</td>
                  <td style={{ padding: "5px 8px", border: "1px solid #0B2D5B" }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <div className="bg-[#0B2D5B] text-white text-[11px] font-bold px-3 py-1.5 uppercase tracking-wide">Account Number (Optional)</div>
          <div style={{ border: "1px solid #0B2D5B", padding: "7px 10px", fontWeight: 700 }}>{account_number}</div>

          <div className="bg-[#0B2D5B] text-white text-[11px] font-bold px-3 py-1.5 uppercase tracking-wide mt-3">Form 1099-NEC Box 1 - Nonemployee Compensation</div>
          <div style={{ background: "#E8F1FF", border: "1px solid #0B2D5B", borderTop: "none", padding: "14px 16px" }} data-testid="receipt-box1-amount">
            <p style={{ color: "#0B2D5B", fontSize: 34, fontWeight: 800, lineHeight: 1 }}>${totals.net_pay.toFixed(2)}</p>
            {(totals.transmission_fees > 0 || totals.bonuses > 0 || totals.deductions > 0) && (
              <p className="text-[10px] text-gray-600 mt-1">
                Gross ${totals.gross_pay.toFixed(2)}
                {totals.transmission_fees > 0 ? ` − Transmission $${totals.transmission_fees.toFixed(2)}` : ""}
                {totals.bonuses > 0 ? ` + Bonus $${totals.bonuses.toFixed(2)}` : ""}
                {totals.deductions > 0 ? ` − Deductions $${totals.deductions.toFixed(2)}` : ""}
              </p>
            )}
          </div>
          <p className="text-[10px] text-gray-500 mt-1.5">Period: <strong>{fmtUS(week.start)}</strong> → <strong>{fmtUS(week.end)}</strong></p>
        </div>
      </div>

      {/* Orders table */}
      <div className="mt-5">
        <table className="w-full" style={{ borderCollapse: "collapse", fontSize: 10.5 }} data-testid="receipt-orders-table">
          <thead>
            <tr style={{ background: "#0B2D5B", color: "#fff" }}>
              <th style={{ padding: "8px 10px", textAlign: "left", width: "13%" }}>ORDER #</th>
              <th style={{ padding: "8px 10px", textAlign: "left", width: "30%" }}>ADDRESS</th>
              <th style={{ padding: "8px 10px", textAlign: "left", width: "18%" }}>CITY</th>
              <th style={{ padding: "8px 10px", textAlign: "left", width: "7%" }}>STATE</th>
              <th style={{ padding: "8px 10px", textAlign: "left", width: "8%" }}>ZIP</th>
              <th style={{ padding: "8px 10px", textAlign: "left", width: "12%" }}>DATE INSPECTED</th>
              <th style={{ padding: "8px 10px", textAlign: "right", width: "10%" }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#999", border: "1px solid #D9E2EF" }}>No completed orders for this period.</td></tr>
            ) : orders.map((o, i) => (
              <tr key={o.order_number + i} style={{ background: i % 2 === 0 ? "#fff" : "#F5F8FC" }}>
                <td style={{ padding: "5px 10px", border: "1px solid #D9E2EF", fontWeight: 600 }}>{o.order_number}</td>
                <td style={{ padding: "5px 10px", border: "1px solid #D9E2EF" }}>{o.address}</td>
                <td style={{ padding: "5px 10px", border: "1px solid #D9E2EF" }}>{o.city}</td>
                <td style={{ padding: "5px 10px", border: "1px solid #D9E2EF" }}>{o.state || "-"}</td>
                <td style={{ padding: "5px 10px", border: "1px solid #D9E2EF" }}>{o.zip}</td>
                <td style={{ padding: "5px 10px", border: "1px solid #D9E2EF" }}>{fmtUS(o.date_inspected)}</td>
                <td style={{ padding: "5px 10px", border: "1px solid #D9E2EF", textAlign: "right", fontWeight: 700, color: "#0B2D5B" }}>${o.amount.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#E8F1FF", fontWeight: 700, color: "#0B2D5B" }}>
              <td colSpan={6} style={{ padding: "8px 10px", textAlign: "right", border: "1px solid #0B2D5B", textTransform: "uppercase", fontSize: 11 }}>Total Nonemployee Compensation</td>
              <td style={{ padding: "8px 10px", border: "1px solid #0B2D5B", textAlign: "right", fontSize: 13 }} data-testid="receipt-footer-total">${totals.gross_pay.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="mt-4 text-[9.5px] text-gray-600 leading-snug">
        This is important tax information and is being furnished to the IRS. If you are required to file a return, a negligence penalty or other sanction may be imposed on you if this income is taxable and the IRS determines that it has not been reported.
      </p>

      <div className="grid grid-cols-2 gap-8 mt-6 items-end">
        <div>
          <p style={{ fontFamily: "'Brush Script MT', cursive", fontSize: 26, color: "#0B2D5B" }}>Thank you for your hard work!</p>
          <p className="text-[11px] text-[#0B2D5B] mt-2">Sincerely,</p>
          <p className="text-[14px] font-extrabold text-[#0B2D5B]">AARIFI LLC</p>
        </div>
        <table className="w-full text-[11px]" style={{ borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ padding: "6px 10px", border: "1px solid #0B2D5B", color: "#0B2D5B", fontWeight: 700, background: "#F0F5FC" }}>Recipient's TIN</td>
              <td style={{ padding: "6px 10px", border: "1px solid #0B2D5B" }}>{inspector.tin_masked || "Not on file"}</td>
            </tr>
            <tr>
              <td style={{ padding: "6px 10px", border: "1px solid #0B2D5B", color: "#0B2D5B", fontWeight: 700, background: "#F0F5FC" }}>Date</td>
              <td style={{ padding: "6px 10px", border: "1px solid #0B2D5B" }}>{fmtUS(week.end)}</td>
            </tr>
            <tr>
              <td style={{ padding: "6px 10px", border: "1px solid #0B2D5B", color: "#0B2D5B", fontWeight: 700, background: "#F0F5FC" }}>Payer's Name</td>
              <td style={{ padding: "6px 10px", border: "1px solid #0B2D5B", fontWeight: 700 }}>AARIFI LLC</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-center text-[10px] text-gray-500 mt-5">Keep this copy for your records.</p>
    </div>
  );
};

/** Modal wrapper — opened from inside Weekly Summary */
export const ReceiptPreviewModal = ({ inspectorName, weekStart, weekEnd, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const res = await axios.get(`${API}/finance/inspector-receipt`, {
          params: { inspector_name: inspectorName, week_start: weekStart, week_end: weekEnd },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Failed to generate receipt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [inspectorName, weekStart, weekEnd]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="receipt-preview-modal">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-[1100px] my-8" onClick={e => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 bg-white rounded-t-xl border-b border-gray-200 sticky top-0 z-10 print:hidden">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#0B2D5B]" />
            <h2 className="text-sm font-bold text-[#0B2D5B]">Payment Receipt — {inspectorName}</h2>
            <span className="text-[10px] text-gray-500">{fmtUS(weekStart)} → {fmtUS(weekEnd)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              disabled={!data}
              className="px-3 py-1.5 bg-[#0B2D5B] hover:bg-[#133b7a] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              data-testid="receipt-modal-print"
            >
              <Printer className="h-3.5 w-3.5" /> Print / Save PDF
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg" data-testid="receipt-modal-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-4">
          {loading && <div className="text-center py-16 text-gray-400 text-sm">Generating receipt...</div>}
          {err && !loading && <div className="text-center py-12 text-[#C07070] text-sm" data-testid="receipt-modal-error">{err}</div>}
          {data && !loading && <ReceiptDocument data={data} />}
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .receipt-print, .receipt-print * { visibility: visible !important; }
          .receipt-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; padding: 10mm !important; max-width: 100% !important; }
          @page { size: Letter; margin: 10mm; }
        }
      `}</style>
    </div>
  );
};

export default ReceiptPreviewModal;
